import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Modal,
  Tab,
  Tabs,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { sxTabs, toBool01 } from "./SospensioniAgendeModalDetails";
import {
  RHFAutocomplete,
  RHFCheckbox,
  RHFDateTimePicker,
  RHFRadioGroup,
} from "../../../../../../../RHFComponents/RHFComponents";
import { useForm, UseFormReturn, useWatch } from "react-hook-form";
import {
  generalResearch,
  pgmedt,
  pgmtgpq,
  ricGPQudia,
} from "../../../../../../../../networking";
import { AllertMessage } from "../MedintAgendaFormDetail";
import { PGMTGPQDATA } from "./AttrGPModal";
import dayjs from "dayjs";

type Props = {
  open: boolean;
  onClose: () => void;
  parentMethods: UseFormReturn<any>;
  initialData?: any;
  createdIdPgmedt?: string;
  action: "insert" | "edit" | "copy";
  onSavedPgmedtData?: () => Promise<void> | void;
  onSavedQuesitiData?: () => void | Promise<PGMTGPQDATA[]>;
};

type QuesitiForm = {
  quesiti: {
    disciplina: string;
    quesitoDiagnostico: string;
    operatoreLogico: string;
    dataInizioValidita: string;
    dataFineValidita: string;
    abbinamentoDisattivato: string;
    codiceDisciplina: string;
    pidAcu: string;
    pgmtgpqPrgint: string;
    pgmtgpqTimestamp: string;
  };
};

// Dati ricevuti dall'api ricerche con tabella NMNSPE
type NMNSPEDATA = {
  codice: string;
  dataFine: string;
  dataInizio: string;
  descrizione: string;
  pkey: string;
  pkeyf: string;
  pkeyl: string;
};

//Tipo dei dati ricevuti da ricGPQudia per i quesiti
type GPQUDIADATA = {
  CODGPQUDIA: string;
  DESGPQUDIA: string;
  DATAINIVAL: string;
  DATAFINEVAL: string;
};

const AttrGPModalDetails = (props: Props) => {
  const idPgmedt =
    props.parentMethods.getValues("medIntNuovoProgramma.chiave") ||
    (props.createdIdPgmedt as string | undefined);

  const [loading, setLoading] = useState<boolean>(false);

  const [showAllert, setShowAllert] = useState(false);
  const [severity, setSeverity] = useState<"warning" | "success" | "error">(
    "warning",
  );
  const [message, setMessage] = useState("");

  const [ricercheData, setRicercheData] = useState<NMNSPEDATA[]>([]);

  const [quesitiData, setQuesitiData] = useState<GPQUDIADATA[]>([]);

  //Recupero dal padre il codice della disciplina, che serve per selezionare il quesito diagnostico.
  //Se non esiste, devo recuperarlo dall'api Ricerche
  const codDisciplina = props.parentMethods.getValues(
    "medIntNuovoProgramma.codDisciplinaQd",
  );

  const modalMethods = useForm<QuesitiForm>({
    defaultValues: {
      quesiti: {
        disciplina: "",
        quesitoDiagnostico:
          props.initialData?.DECODE_GPQUDIA_GPQUDIA_DESCRIZIONE ?? "",
        operatoreLogico: props.initialData?.PGMTGPQ_OP_LOGICO ?? "",
        codiceDisciplina: props?.initialData?.GPQUDIA_CODICE ?? "",
        dataInizioValidita: props.initialData?.PGMTGPQ_DT_INI_VAL ?? "",
        dataFineValidita: props.initialData?.PGMTGPQ_DT_FIN_VAL ?? "",
        abbinamentoDisattivato:
          props.initialData?.PGMTGPQ_SW_DISABILITATO ?? "",
        pidAcu: props.initialData?.PGMTGPQ_PID_ACU ?? "",
        pgmtgpqPrgint: props.initialData?.PGMTGPQ_PRGINT ?? "",
        pgmtgpqTimestamp: props.initialData?.PGMTGPQ_TIMESTAMP ?? "",
      },
    },
  });

  const disciplinaCode = useWatch({
    control: modalMethods.control,
    name: "quesiti.disciplina",
  });

  const quesitoSelected = useWatch({
    control: modalMethods.control,
    name: "quesiti.quesitoDiagnostico",
  });

  useEffect(() => {
    if (!disciplinaCode) return;

    const dataRif = props.parentMethods.getValues(
      "medIntNuovoProgramma.dataIni",
    );
    if (!dataRif) return;

    // Variabile per evitare di chiamare la funzione più volte
    let alive = true;

    const getQuesitiList = async () => {
      try {
        const response = await ricGPQudia({
          disciplinaQD: disciplinaCode,
          dataRif: dayjs().format("YYYY-MM-DD"),
          pgmedt_prgint: idPgmedt ?? "",
          codAgendaGP: "",
        });

        setQuesitiData(response.data.dati.gpqudia ?? []);
      } catch (e) {
        console.error(e);
      }
    };

    getQuesitiList();
    // Quando il componente viene chiuso, imposto la variabile a false per evitare rerender
    return () => {
      alive = false;
    };
  }, [disciplinaCode, idPgmedt, props.parentMethods]);

  //Funzione per ottenere la disciplina nel caso non esista
  const getCodDisciplina = async () => {
    const response = await generalResearch({
      tabella: "NMNSPE",
      codice: "",
      filtro: "",
      applicazione: "",
      attivi: "0",
    });

    const data = response.data.dati as any[];

    //Mappo i dati ricevuti dall'api per avere l'oggetto già normalizzato
    const normalized: NMNSPEDATA[] = data.map((x) => ({
      pkey: x.pkey,
      pkeyf: x.pkeyf,
      pkeyl: x.pkeyl,
      codice: x.codice,
      descrizione: x.descrizione,
      dataInizio: x["data inizio validit"] ?? x.dataInizio ?? "",
      dataFine: x["data fine validit"] ?? x.dataFine ?? "",
    }));

    setRicercheData(normalized);
  };

  useEffect(() => {
    if (!props.open) {
      return;
    }
    getCodDisciplina();

    if (props.action === "insert") {
      modalMethods.reset({
        quesiti: {
          disciplina: codDisciplina ?? "",
          quesitoDiagnostico: "",
          operatoreLogico: "",
          codiceDisciplina: codDisciplina,
          dataInizioValidita: "",
          dataFineValidita: "",
          abbinamentoDisattivato: "",
          pidAcu: "",
          pgmtgpqPrgint: "",
          pgmtgpqTimestamp: "",
        },
      });
      return;
    }

    console.log(props.initialData);

    modalMethods.reset({
      quesiti: {
        disciplina: codDisciplina ?? "",
        quesitoDiagnostico: props.initialData?.GPQUDIA_CODICE ?? "",
        operatoreLogico: props.initialData?.PGMTGPQ_OP_LOGICO ?? "",
        codiceDisciplina: codDisciplina ?? "",
        dataInizioValidita: props.initialData?.PGMTGPQ_DT_INI_VAL ?? "",
        dataFineValidita: props.initialData?.PGMTGPQ_DT_FIN_VAL ?? "",
        abbinamentoDisattivato:
          props.initialData?.PGMTGPQ_SW_DISABILITATO ?? "",
        pidAcu: props.initialData?.PGMTGPQ_PID_ACU ?? "",
        pgmtgpqPrgint: props.initialData?.PGMTGPQ_PRGINT ?? "",
        pgmtgpqTimestamp: props.initialData?.PGMTGPQ_TIMESTAMP ?? "",
      },
    });
  }, [modalMethods.reset, props.action, props.initialData, props.open]);

  const prevDisciplinaRef = useRef<string>("");

  //Se l’utente cambia disciplina, resetto il quesito perché non è più valido
  useEffect(() => {
    if (!props.open) return;

    const prev = prevDisciplinaRef.current;

    // Alla prima assegnazione non resetto
    if (!prev) {
      prevDisciplinaRef.current = disciplinaCode ?? "";
      return;
    }

    // Quando l'utente cambia la disciplina resetto il quesito
    if (disciplinaCode && disciplinaCode !== prev) {
      const currentQuesito = modalMethods.getValues(
        "quesiti.quesitoDiagnostico",
      );
      if (currentQuesito) {
        modalMethods.setValue("quesiti.quesitoDiagnostico", "", {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
    // Salvo la disciplina
    prevDisciplinaRef.current = disciplinaCode ?? "";
  }, [disciplinaCode, props.open, modalMethods]);

  useEffect(() => {
    if (!showAllert) return;
    if (severity === "warning") return;

    const t1 = setTimeout(() => setShowAllert(false), 4000);
    const t2 = setTimeout(() => {
      setSeverity("warning");
      setMessage("");
    }, 6000);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [showAllert, severity]);

  const askConfirmSubmit = () => {
    setMessage(
      props.action === "insert"
        ? "Confermare l'inserimento del quesito?"
        : "Confermare la modifica del quesito?",
    );
    setSeverity("warning");
    setShowAllert(true);
  };

  const handleSubmit = async (data: QuesitiForm) => {
    setLoading(true);

    const isInsert = props.action === "insert";
    const parentForm = props.parentMethods.getValues("medIntNuovoProgramma");

    try {
      const responseSubmit = await pgmtgpq({
        crud: isInsert ? "c" : "u",
        ...(!isInsert ? { prgint: props.initialData?.PGMTGPQ_PRGINT } : {}),
        dati: {
          pgmtgpq: {
            PGMEDT_PRGINT: idPgmedt,
            GPQUDIA_CODICE: data.quesiti.quesitoDiagnostico,
            PGMTGPQ_SW_DISABILITATO: toBool01(
              data.quesiti.abbinamentoDisattivato,
            )
              ? "1"
              : "0",
            PGMTGPQ_OP_LOGICO: data.quesiti.operatoreLogico,
            PGMTGPQ_DT_INI_VAL: data.quesiti.dataInizioValidita,
            PGMTGPQ_DT_FIN_VAL: data.quesiti.dataFineValidita,
            PGMTGPQ_PID_ACU: data.quesiti.pidAcu,
            PGMTGPQ_TIMESTAMP: data.quesiti.pgmtgpqTimestamp,
          },
        },
      });

      if (responseSubmit?.data?.esito === "OK") {
        await pgmedt({
          crud: "u",
          prgint: idPgmedt,
          dati: {
            pgmedt: {
              PGMEDT_PRGINT: idPgmedt,
              PGMEDT_CODICE: parentForm.codProg ?? "",
              DATA_INI_VAL: parentForm.dataIni ?? "",
              DATA_FINE_VAL: parentForm.dataFin ?? "",
              PGMEDT_DESCR: parentForm.descrCompl ?? "",
              PGMEDT_DESC_RIDOT: parentForm.descrRid ?? "",
              PGMEDT_AP_ULT_ORA: parentForm.appUltFascOra ? "S" : "N",
              PGMEDT_DES_LUN: parentForm.note?.lunedi ?? "",
              PGMEDT_DES_MAR: parentForm.note?.martedi ?? "",
              PGMEDT_DES_MER: parentForm.note?.mercoledi ?? "",
              PGMEDT_DES_GIO: parentForm.note?.giovedi ?? "",
              PGMEDT_DES_VEN: parentForm.note?.venerdi ?? "",
              PGMEDT_DES_SAB: parentForm.note?.sabato ?? "",
              PGMEDT_DES_DOM: parentForm.note?.domenica ?? "",
              PGMEDT_DES_GEN: parentForm.note?.standard ?? "",
              NMNSPE_CODICE: data.quesiti.disciplina ?? "",
              PGMEDT_NORMALIZZAZIONE: "",
              PGMEDT_OP_LOGICO_QD: data.quesiti.operatoreLogico,
              PGMEDT_SW_RAGGRUPPABILE: "",
              NMNSPE_AMM_CODICE: data.quesiti.disciplina ?? "",
              PGMEDT_SW_ALTRI_ORARI: "",
              PGMEDT_SW_SOLO_GP: "0",
              PGMEDT_SW_AGGIUNTIVA: "",
            },
          },
        });

        setSeverity("success");
        setMessage(responseSubmit?.data?.messaggio ?? "");

        await props?.onSavedQuesitiData?.();
        await props?.onSavedPgmedtData?.();
      } else {
        setSeverity("error");
        setMessage(responseSubmit?.data?.messaggio ?? "");
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "rgba(255, 255, 255, 0.7)",
            zIndex: 9999,
          }}
        >
          <CircularProgress size="6rem" />
        </Box>
      )}
      <AllertMessage
        showAllert={showAllert}
        setShowAllert={setShowAllert}
        severity={severity}
        message={message}
        submitFunction={() => {
          setShowAllert(false);
          modalMethods.handleSubmit(handleSubmit)();
        }}
      />
      <Modal
        open={props.open}
        onClose={(_, reason) => {
          if (loading) return;
          // blocco chiusura su click fuori
          if (reason === "backdropClick") return;

          // blocco su tasto ESC
          if (reason === "escapeKeyDown") return;

          props.onClose();
        }}
      >
        <Box
          sx={{
            width: "92vw",
            maxWidth: 980,
            maxHeight: "90vh",
            overflow: "auto",
            bgcolor: "white",
            borderRadius: 2,
            boxShadow: 24,
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            p: 2,
          }}
        >
          <Tabs
            value={0}
            sx={{ px: 1, ...sxTabs }}
            TabIndicatorProps={{ sx: { height: 3 } }}
          >
            <Tab
              label="Quesiti - Dettaglio"
              sx={{
                fontFamily: "Montserrat",
                fontWeight: 700,
                textTransform: "none",
              }}
            />
          </Tabs>
          <Grid container spacing={2} p={2}>
            <Grid item xs={12} md={6}>
              <RHFAutocomplete
                control={modalMethods.control}
                name="quesiti.disciplina"
                label="Disciplina"
                fullWidth
                options={ricercheData ?? []}
                rules={{ required: "Campo obbligatorio" }}
                getOptionLabel={(option) => option.descrizione}
                mapIn={(code, opts) =>
                  opts.find(
                    (o: any) => String(o.codice).trim() === String(code).trim(),
                  ) ?? null
                }
                mapOut={(opt: any) => String(opt?.codice ?? "")}
                disabled={Boolean(
                  props.parentMethods.getValues(
                    "medIntNuovoProgramma.codDisciplinaQd",
                  ),
                )}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFAutocomplete
                control={modalMethods.control}
                name="quesiti.quesitoDiagnostico"
                label="Quesito Diagnostico"
                fullWidth
                options={quesitiData ?? []}
                getOptionLabel={(q: any) =>
                  `${String(q.CODGPQUDIA ?? "").trim()} - ${String(q.DESGPQUDIA ?? "").trim()}`
                }
                mapIn={(code, opts) =>
                  opts.find(
                    (o: any) =>
                      String(o.CODGPQUDIA).trim() === String(code).trim(),
                  ) ?? null
                }
                mapOut={(opt: any) => String(opt?.CODGPQUDIA ?? "")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box sx={{ minWidth: 140, fontWeight: 500 }}>
                  Operatore logico
                </Box>
                <RHFRadioGroup
                  control={modalMethods.control}
                  name="quesiti.operatoreLogico"
                  row
                  options={[
                    { value: "U", label: "Uguale" },
                    { value: "D", label: "Diverso" },
                  ]}
                  rules={{ required: "Campo obbligatorio" }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} md={6}></Grid>
            <Grid item xs={12} md={6}>
              <RHFDateTimePicker
                control={modalMethods.control}
                name="quesiti.dataInizioValidita"
                label="Data Inizio Validita"
                fullWidth
                dateOnly
                displayFormat="DD/MM/YY"
                valueFormat="YYYY-MM-DD"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFDateTimePicker
                control={modalMethods.control}
                name="quesiti.dataFineValidita"
                label="Data Fine Validita"
                fullWidth
                dateOnly
                displayFormat="DD/MM/YY"
                valueFormat="YYYY-MM-DD"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFCheckbox
                control={modalMethods.control}
                name="quesiti.abbinamentoDisattivato"
                label="Abbinamento Disattivato"
              />
            </Grid>
            <Grid item xs={12} md={6}></Grid>
            <Grid
              item
              xs={12}
              md={12}
              display={"flex"}
              justifyContent={"space-between"}
            >
              <Button
                variant="outlinedCustom"
                size="small"
                onClick={props.onClose}
                disabled={loading}
              >
                {" "}
                Indietro
              </Button>
              <Button
                variant="containedCustom"
                size="small"
                onClick={askConfirmSubmit}
                disabled={loading}
              >
                {" "}
                Conferma
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Modal>
    </>
  );
};

export default AttrGPModalDetails;
